import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { GoogleMap, PolygonF, Marker } from "@react-google-maps/api";
import { isNotNullOrEmptyArray } from "../globals/utils";
import {
  DEFAULT_LOCATION,
  GEOLOCATION_OPTIONS,
  getValidCenter,
  createDefaultPolygon,
} from "../utils/mapUtils";

const LocateLand = ({
  center,
  path,
  setPath,
  mapHeight,
  setCenter,
  editable,
  currentLocation,
  setCurrentLocation,
  createInitialRectangle,
  isScriptLoaded,
  forceRecenter,
  preventAutoCenter = false, // New prop to prevent auto-centering
}) => {
  const polygonRef = useRef(null);
  const listenersRef = useRef([]);
  const mapRef = useRef(null);
  const lastValidCenterRef = useRef(null);

  const mapContainerStyle = useMemo(
    () => ({
      width: "100%",
      height: mapHeight ? mapHeight : "300px",
    }),
    [mapHeight]
  );

  // Handle map load
  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (
      createInitialRectangle &&
      currentLocation?.latitude &&
      currentLocation?.longitude
    ) {
      const rectanglePath = createDefaultPolygon(
        currentLocation.latitude,
        currentLocation.longitude
      );
      rectanglePath.push(rectanglePath[0]);
      setPath(rectanglePath);
      setCenter({
        lat: currentLocation.latitude,
        lng: currentLocation.longitude,
      });
    }
  }, [createInitialRectangle, setPath, setCenter]);

  useEffect(() => {
    let watchId;

    const handleSuccess = (position) => {
      const { latitude, longitude, accuracy } = position.coords;
      setCurrentLocation({ latitude, longitude, accuracy });
    };

    const handleError = (error) => {
      console.warn("Error tracking location:", error);

      // Handle specific error cases
      switch (error.code) {
        case error.TIMEOUT:
          navigator.geolocation.getCurrentPosition(
            handleSuccess,
            (fallbackError) => {
              console.error("Fallback location failed:", fallbackError);
              setCurrentLocation(DEFAULT_LOCATION);
            },
            { ...GEOLOCATION_OPTIONS, timeout: 30000 }
          );
          break;

        case error.PERMISSION_DENIED:
          console.error("Location permission denied by user");
          setCurrentLocation(DEFAULT_LOCATION);
          break;

        case error.POSITION_UNAVAILABLE:
          console.error("Location information unavailable");
          setCurrentLocation(DEFAULT_LOCATION);
          break;

        default:
          setCurrentLocation(DEFAULT_LOCATION);
      }
    };

    // Start watching position
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        handleSuccess,
        handleError,
        GEOLOCATION_OPTIONS
      );
    } else {
      console.error("Geolocation not supported");
      setCurrentLocation(DEFAULT_LOCATION);
    }

    // Cleanup function
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [setCurrentLocation]);

  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const nextPath = polygonRef.current
        .getPath()
        .getArray()
        .map((latLng) => {
          return { lat: latLng.lat(), lng: latLng.lng() };
        });
      setPath(nextPath);
    }
  }, [setPath]);

  const onUnmount = useCallback(() => {
    listenersRef.current.forEach((lis) => lis.remove());
    polygonRef.current = null;
  }, []);

  const onLoad = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
      const pathListener = polygon.getPath();
      listenersRef.current.push(
        pathListener.addListener("set_at", onEdit),
        pathListener.addListener("insert_at", onEdit),
        pathListener.addListener("remove_at", onEdit)
      );
    },
    [onEdit]
  );

  const handleCenterChanged = useCallback(() => {
    if (!mapRef.current || preventAutoCenter) return;
    const newPos = mapRef.current.getCenter().toJSON();
    setCenter(newPos);
  }, [setCenter, preventAutoCenter]);

  const LoadingComponent = () => (
    <div
      style={{
        height: "300px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        border: "1px solid #ddd",
        borderRadius: "4px",
      }}
    >
      Loading map...
    </div>
  );

  const validCenter = useMemo(() => {
    const newCenter = getValidCenter(null, center, currentLocation);
    // Only update if significantly different to prevent jittering
    if (lastValidCenterRef.current) {
      const latDiff = Math.abs(newCenter.lat - lastValidCenterRef.current.lat);
      const lngDiff = Math.abs(newCenter.lng - lastValidCenterRef.current.lng);
      if (latDiff < 0.0001 && lngDiff < 0.0001) {
        return lastValidCenterRef.current;
      }
    }
    lastValidCenterRef.current = newCenter;
    return newCenter;
  }, [center, currentLocation]);

  useEffect(() => {
    if (forceRecenter && mapRef.current && validCenter && !preventAutoCenter) {
      mapRef.current.panTo({ lat: validCenter.lat, lng: validCenter.lng });
      mapRef.current.setZoom(15);
    }
  }, [forceRecenter, validCenter, preventAutoCenter]);

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: !editable,
      mapTypeId: "hybrid",
      gestureHandling: "greedy",
      zoomControl: true,
    }),
    [editable]
  );

  return (
    <>
      {isScriptLoaded ? (
        <GoogleMap
          id="circle-example"
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          center={validCenter}
          onLoad={handleMapLoad}
          onCenterChanged={handleCenterChanged}
          options={mapOptions}
        >
          {isNotNullOrEmptyArray(path) && (
            <PolygonF
              paths={path}
              editable={editable}
              draggable={editable}
              onMouseUp={onEdit}
              onDragEnd={onEdit}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                strokeColor: "#FF0000",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#FF0000",
                fillOpacity: 0.35,
              }}
            />
          )}
          {!editable && validCenter && (
            <Marker position={{ lat: validCenter.lat, lng: validCenter.lng }} />
          )}
          {currentLocation?.latitude && (
            <Marker
              position={{
                lat: currentLocation.latitude,
                lng: currentLocation.longitude,
              }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                scale: 7,
                fillColor: "#4285F4",
                fillOpacity: 1,
                strokeColor: "white",
                strokeWeight: 2,
              }}
            />
          )}
        </GoogleMap>
      ) : (
        <LoadingComponent />
      )}
    </>
  );
};

export default LocateLand;
